import {useEffect, useMemo, useRef, useState} from 'react';
import useInterval from './useInterval';
import {fetchData} from '../libraries/DnaApi';

const POLL_INTERVAL = 10000; // ms — matches SongCard's original cadence
const MAX_RETRIES = 10;

const isAnalyzed = (song) => song && song.latest_analysis && song.latest_analysis.status === 'ANALYZED';
const isAnalyzing = (song) => !song.latest_analysis || song.latest_analysis.status === 'ANALYZING';

/**
 * Fetch + poll a song's similars (the recommendation engine), lifted out of
 * SongCard so both the card strip and the redesigned player can consume it.
 *
 * Mirrors SongCard's original logic: seed from `latest_analysis.similars` when
 * already ANALYZED, otherwise poll `/songs/{id}/similars` (200 = ready,
 * 202 = still analyzing → keep polling up to MAX_RETRIES, 422/404 = give up).
 *
 * @param {object} song  song-like object; needs `.id`, optionally `.latest_analysis`
 * @param {object} [opts]
 * @param {boolean} [opts.sort]  return a copy sorted by `proximity` desc (player wants this)
 * @returns {{similars: (Array|null), loading: boolean, message: string}}
 */
export default function useSimilars(song, {sort = false, params = ''} = {}) {
    const songId = (song && song.id) || null;
    // Recommendations are catalog tracks (type 'catalog'); user uploads are 'song'.
    // Route to the matching similars endpoint — catalog ids don't exist under /songs.
    const resource = song && song.type === 'catalog' ? 'catalogs' : 'songs';

    const [similars, setSimilars] = useState(() => (isAnalyzed(song) ? (song.latest_analysis.similars || []) : null));
    const [loading, setLoading] = useState(() => Boolean(songId) && !isAnalyzed(song));
    const [message, setMessage] = useState('Analyzing');
    const retriesRef = useRef(0);

    const poll = () => {
        // Only poll while the song is still analyzing.
        if (!songId || !isAnalyzing(song)) {
            return;
        }
        retriesRef.current += 1;
        const attempt = retriesRef.current;

        fetchData('/' + resource + '/' + songId + '/similars' + (params ? '?' + params : '')).then((response) => {
            if (!response || !response.status) {
                console.error('unattended response', response);
                return;
            }
            switch (response.status) {
                case 200:
                    setSimilars(response.data.data);
                    setLoading(false);
                    break;
                case 202:
                    if (attempt >= MAX_RETRIES) {
                        setMessage('Processing is too long, the file might need to be re-encoded');
                        setSimilars([]);
                        setLoading(false);
                    }
                    break;
                case 422:
                    setMessage('Error processing file, try re-encoding it');
                    console.error('Error processing the file, please try to re-encode');
                    break;
                case 404:
                default:
                    setMessage('Processing problem on server-side, please retry');
                    setSimilars([]);
                    setLoading(false);
                    break;
            }
        });
    };

    // Seed (or reset + kick off polling) whenever the song changes.
    useEffect(() => {
        retriesRef.current = 0;
        setMessage('Analyzing');

        if (!songId) {
            setSimilars([]);
            setLoading(false);
            return;
        }
        if (isAnalyzed(song)) {
            setSimilars(song.latest_analysis.similars || []);
            setLoading(false);
            return;
        }
        // Not analyzed yet — fetch immediately, then let useInterval poll.
        setSimilars(null);
        setLoading(true);
        poll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [songId, params]);

    useInterval(poll, POLL_INTERVAL, loading);

    const songType = song && song.type;
    // Memoised so the returned array keeps a stable identity across renders —
    // consumers (e.g. the Up Next radio) put it in effect deps, and a fresh
    // array every render would loop. Only recomputes when the data changes.
    const result = useMemo(() => {
        if (!Array.isArray(similars)) {
            return similars;
        }
        // A catalog track is its own #1 similar (proximity ~1.0) — drop the
        // now-playing item so it doesn't recommend itself. Match on id+type to
        // avoid colliding with an upload that happens to share a catalog id.
        const visible = similars.filter((s) => !(s && s.id === songId && s.type === songType));
        return sort ? [...visible].sort((a, b) => (b.proximity || 0) - (a.proximity || 0)) : visible;
    }, [similars, sort, songId, songType]);

    return {similars: result, loading, message};
}
